import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Grid,
  Button,
  CircularProgress,
  Backdrop,
  Snackbar,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import Alert from 'src/components/Alert';
import SearchIcon from '@material-ui/icons/Search';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ListAltIcon from '@material-ui/icons/ListAlt';
import ReceiptIcon from '@material-ui/icons/Receipt';
import StorageIcon from '@material-ui/icons/Storage';
import SettingsIcon from '@material-ui/icons/Settings';
import SecurityIcon from '@material-ui/icons/Security';
import BuildIcon from '@material-ui/icons/Build';
import LaunchIcon from '@material-ui/icons/Launch';
import { useDispatch, useSelector } from 'react-redux';
import { getPartnerReports, savePartnerReportGuid } from 'src/store/slices/partnerReportSlice';
import { getUser } from 'src/utils/Common';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(5)
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  searchField: {
    minWidth: 260,
    backgroundColor: '#ffffff',
    borderRadius: 4,
    '& .MuiOutlinedInput-root': {
      borderRadius: 4,
      borderColor: '#cbd5e1'
    }
  },
  categoryNav: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  categoryBtn: {
    borderRadius: 4,
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: '#1D3343',
    backgroundColor: '#ffffff',
    borderColor: '#cbd5e1',
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#94a3b8'
    }
  },
  categoryBtnActive: {
    borderRadius: 4,
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: '#ffffff !important',
    backgroundColor: '#1D3343 !important',
    borderColor: '#1D3343 !important',
    boxShadow: '0 2px 4px rgba(29, 51, 67, 0.2)',
    '&:hover': {
      backgroundColor: '#14232e !important'
    }
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  gridItem: {
    display: 'flex'
  },
  // Option 1: Clean Minimal Vertical Enterprise Card (Fixed Equal Heights)
  reportCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
    '&:hover': {
      borderColor: '#1D3343',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(29, 51, 67, 0.08)',
      '& $iconBox': {
        backgroundColor: '#e2e8f0',
        color: '#007DBA'
      },
      '& $launchIcon': {
        color: '#007DBA',
        transform: 'translate(2px, -2px)'
      }
    }
  },
  cardTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5)
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#1D3343',
    transition: 'all 0.2s ease'
  },
  launchIcon: {
    fontSize: 18,
    color: '#cbd5e1',
    transition: 'all 0.2s ease'
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: '#1D3343',
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.75),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  cardDesc: {
    fontSize: 13,
    color: '#64748b',
    lineHeight: 1.45,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: 38
  },
  emptyContainer: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: '#64748b'
  }
}));

const getCategoryIcon = (categoryId) => {
  switch (categoryId) {
    case 1:
      return <BuildIcon style={{ fontSize: 22 }} />; // Spare Part
    case 2:
      return <StorageIcon style={{ fontSize: 22 }} />; // SAP Integration
    case 3:
      return <SettingsIcon style={{ fontSize: 22 }} />; // Config Reports
    case 4:
      return <AssessmentIcon style={{ fontSize: 22 }} />; // Data Reports
    case 5:
      return <ReceiptIcon style={{ fontSize: 22 }} />; // Payment
    case 6:
      return <SecurityIcon style={{ fontSize: 22 }} />;
    default:
      return <ListAltIcon style={{ fontSize: 22 }} />;
  }
};

function PartnerReport() {
  const classes = useStyles();
  const { t } = useTranslation('overview');
  const dispatch = useDispatch();
  const partnerReportState = useSelector(state => state.partnerReport || {});
  const partnerReports = partnerReportState.reports || [];
  const loading = partnerReportState.loading || false;

  const [activeCategory, setActiveCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [uyari, setUyari] = useState(false);
  const [uyariTip, setUyariTip] = useState('default');
  const [responseMessage, setResponseMessage] = useState({
    ErrorCode: '0',
    ErrorDescription: t('success_message')
  });

  const SessionUser = getUser();

  useEffect(() => {
    dispatch(getPartnerReports());
  }, [dispatch]);

  // Extract distinct categories sorted by Id (1++)
  const categories = useMemo(() => {
    const categoryMap = new Map();
    partnerReports.forEach(report => {
      const catId = report.CategoryId || report.categoryId;
      const catName = report.CategoryName || report.categoryName;
      if (catId && catName && !categoryMap.has(catId)) {
        categoryMap.set(catId, {
          id: catId,
          name: catName
        });
      }
    });
    return Array.from(categoryMap.values()).sort((a, b) => a.id - b.id);
  }, [partnerReports]);

  // Normalize reports
  const allReports = useMemo(() => {
    return partnerReports.map(report => {
      const catId = report.CategoryId || report.categoryId || 0;
      const catName = report.CategoryName || report.categoryName || 'General';
      return {
        Id: report.Id || report.id,
        title: report.Name || report.name,
        description: report.Description || report.description,
        link: report.Link || report.link,
        categoryId: catId,
        categoryName: catName,
        icon: getCategoryIcon(catId)
      };
    });
  }, [partnerReports]);

  // Filter reports by active category and search term
  const filteredReports = useMemo(() => {
    return allReports.filter(report => {
      const matchesCategory =
        activeCategory === 'ALL' || report.categoryId === activeCategory;
      const matchesSearch =
        !searchTerm.trim() ||
        (report.title &&
          report.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (report.description &&
          report.description.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [allReports, activeCategory, searchTerm]);

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') return;
    setUyari(false);
  };

  const handleReportClick = (Id) => {
    const report = allReports.find(r => r.Id === Id);
    if (!report) return;

    const dto = {
      PartnerReportId: Id,
      EmployeeId: SessionUser.TechnicianId,
      ServiceShopId: SessionUser.ServiceShopId,
      TenantId: SessionUser.TenantId
    };

    dispatch(savePartnerReportGuid(dto))
      .then(response => {
        const isSuccess =
          response?.payload?.Code === '200' ||
          response?.payload?.ErrorCode === '0';
        const guid = response?.payload?.ResponseObject?.Guid;

        if (!isSuccess || !guid) {
          setResponseMessage({
            ErrorCode: '1',
            ErrorDescription:
              response?.payload?.Message ||
              t('An error occurred while saving the report')
          });
          setUyariTip('error');
          setUyari(true);
          return;
        }

        const link = report.link || '';
        const separator = link.includes('?')
          ? link.endsWith('&') || link.endsWith('?')
            ? ''
            : '&'
          : '?';

        window.open(`${link}${separator}Guid=${guid}`, '_blank');
      })
      .catch(error => {
        setResponseMessage({
          ErrorCode: '1',
          ErrorDescription:
            error.message || t('An error occurred while saving the report')
        });
        setUyariTip('error');
        setUyari(true);
      });
  };

  return (
    <Page title={t('Partner Report')}>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={uyari} autoHideDuration={10000} onClose={uyariKapat}>
        <Alert
          onClose={uyariKapat}
          message={responseMessage.ErrorDescription}
          variant={uyariTip}
        />
      </Snackbar>

      <Container maxWidth="xl" className={classes.root}>
        {/* Category Navigation & Search */}
        <div className={classes.headerSection}>
          <div className={classes.topBar}>
            {/* Category Filter Pills (Ordered 1++) */}
            <div className={classes.categoryNav}>
              <Button
                variant={activeCategory === 'ALL' ? 'contained' : 'outlined'}
                className={
                  activeCategory === 'ALL'
                    ? classes.categoryBtnActive
                    : classes.categoryBtn
                }
                onClick={() => setActiveCategory('ALL')}
              >
                {t('ALL')} ({allReports.length})
              </Button>

              {categories.map(cat => {
                const count = allReports.filter(
                  r => r.categoryId === cat.id
                ).length;
                return (
                  <Button
                    key={cat.id}
                    variant={
                      activeCategory === cat.id ? 'contained' : 'outlined'
                    }
                    className={
                      activeCategory === cat.id
                        ? classes.categoryBtnActive
                        : classes.categoryBtn
                    }
                    onClick={() => setActiveCategory(cat.id)}
                  >
                    {t(cat.name)} ({count})
                  </Button>
                );
              })}
            </div>

            {/* Quick Search Field */}
            <TextField
              size="small"
              variant="outlined"
              placeholder={t('Search report...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#94a3b8', fontSize: 18 }} />
                  </InputAdornment>
                )
              }}
            />
          </div>
        </div>

        {/* Report Cards Grid */}
        {filteredReports.length === 0 ? (
          <Box className={classes.emptyContainer}>
            <Typography variant="body1">{t('No reports found')}</Typography>
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {filteredReports.map(report => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={report.Id}
                className={classes.gridItem}
              >
                <Box
                  className={classes.reportCard}
                  onClick={() => handleReportClick(report.Id)}
                >
                  <div className={classes.cardTopRow}>
                    <div className={classes.iconBox}>{report.icon}</div>
                    <LaunchIcon className={classes.launchIcon} />
                  </div>
                  <Typography className={classes.cardTitle} title={report.title}>
                    {t(report.title)}
                  </Typography>
                  <Tooltip
                    title={report.description || ''}
                    arrow
                    placement="bottom-start"
                  >
                    <Typography className={classes.cardDesc}>
                      {t(report.description)}
                    </Typography>
                  </Tooltip>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Page>
  );
}

export default PartnerReport;
